import HealthMonitor from '@omni/kit/HealthMonitor';
import {
  KitBadge,
  KitIcon,
  KitListItem,
  KitLoader,
  KitText,
} from '@omni/kit/components';
import KitAvatar from '@omni/kit/components/KitAvatar';
import KitList from '@omni/kit/components/KitList';
import KitModal from '@omni/kit/components/KitModalV2/KitModalV2';
import KitNotFound from '@omni/kit/components/KitNotFound';
import { KitSnackDuration } from '@omni/kit/components/KitSnack.types';
import KitSnackRender from '@omni/kit/components/KitSnackRender';
import KitTab from '@omni/kit/components/KitTab';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import {
  SizeClass,
  SizeClassV2,
  useSizeClass,
} from '@omni/kit/contexts/SizeClassContext';
import { useFeatureFlag } from '@omni/kit/hooks/useFeatureFlag';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import GroupsService from '@omni/kit/services/GroupsService';
import {
  IGroupAttendances,
  IGroupEvent,
  IGroupHeadCounts,
  IGroupLocation,
  IGroupMember,
} from '@omni/kit/services/GroupsService/Types';
import { UNLEASH_TOGGLES } from '@omni/kit/services/UnleashService';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { colorForScheme } from '@omni/kit/theming/Theming';
import { excludedStatusCodes } from '@omni/kit/utilities/excludedStatusCodes';
import { createImageUrl } from '@omni/kit/utilities/utilities';
import {
  NavigationProp,
  ParamListBase,
  RouteProp,
  useIsFocused,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import moment from 'moment';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Keyboard, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { SendbirdUserMessage } from '../../../Types';
import useGetMessageIdForEventId from '../../../hooks/useGetMessageIdForEventId';
import { useTakeAttendanceQuery } from '../../../hooks/useTakeAttendanceQuery';
import { sortMembers } from '../../../utilities/sortGroupMembers';
import * as Constants from '../../Constants';
import { sendEventMessage } from '../../redux/actions/ChatActions';
import {
  channelSelector,
  ownerListSelector,
  userSelector,
} from '../../redux/selectors';
import CreateEditGroupEventScreen from '../channelDetails/CreateEditGroupEventScreen';
import CreateOrUpdateGroupAttendance from './components/CreateOrUpdateGroupAttendance';
import DeleteEventConfirmModal from './components/DeleteEventConfirmModal';
import EventAttendanceBarComponent from './components/EventAttendanceBarComponent';
import EventAttendanceComponent from './components/EventAttendanceComponent';
import EventDetailHeader from './components/EventDetailHeader';
import EventDetailLocation from './components/EventDetailLocation';
import MoreActionModal from './components/MoreActionModal';
import ShareEventConfirmModal from './components/ShareEventConfirmModal';

const debug = require('debug')('tca:chat:screen:EventDetailsScreen');
type EventServiceFunction = (params: {
  body?: any;
  token?: string;
  id: string;
}) => Promise<any>;

export interface RouteProps extends RouteProp<ParamListBase, string> {
  params: {
    eventId: any;
    activeMessage: SendbirdUserMessage;
    isRepeatingEvent: boolean;
    takeAttendance?: boolean;
  };
}

export default function EventDetailsScreen(): ReactElement {
  // Navigation
  let navigation: NavigationProp<ParamListBase>;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigation = useNavigation();
  } catch {
    navigation = {} as NavigationProp<ParamListBase>; // Provide a fallback object
  }
  const { t } = useTranslation(['groups']);
  // Routes
  let route: RouteProps;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    route = useRoute<RouteProps>();
  } catch {
    route = {} as RouteProps;
  }
  const dispatch = useDispatch();
  const { sizeClassV2, windowHeight, windowWidth } = useSizeClass();
  const { sizeClass } = useSizeClass();
  const isSmall = sizeClass === SizeClass.Small;
  const { app } = useShellContext();
  const appKey = app.appKey;
  const { tokens, user } = useShellContext();
  const accessToken = useMemo(() => tokens.user || '', [tokens.user]);
  const [isLoading, setIsLoading] = useState(false);
  const takeAttendanceQuery = useTakeAttendanceQuery();
  const [members, setMembers] = useState<IGroupMember[]>([]);
  let pathEventId: string | undefined;

  if (Platform.OS === 'web' && !isSmall) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const webParams = useParams<{ channelId: string; eventId: string }>();

    pathEventId = webParams.eventId;
  }

  const eventId = route?.params?.eventId ?? pathEventId;
  const history = useHistory();

  const {
    activeMessage = {},
    isRepeatingEvent = false,
    takeAttendance,
  } = route?.params ?? {};
  const [activeTab, setActiveTab] = useState(0);
  const isLarge = sizeClassV2 >= SizeClassV2.L;
  const isLandscape = windowHeight < windowWidth;

  const [snackOptions, setSnackOptions] = useState({
    visible: false,
    message: '',
    duration: KitSnackDuration.SHORT,
  });

  const [groupEvent, setGroupEvent] = useState<IGroupEvent | undefined>();
  const [repeatingGroupEvent, setRepeatingGroupEvent] = useState<
    IGroupEvent | undefined
  >();
  const [moreActionModal, setMoreActionModal] = useState<boolean>(false);
  const [eventToDelete, setEventToDelete] = useState<boolean>(false);
  const [eventToShare, setEventToShare] = useState<boolean>(false);
  const [headcounts, setHeadcounts] = useState<IGroupHeadCounts | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoadingHeadCounts, setIsLoadingHeadCounts] = useState(true);
  const [groupEventAttendances, setGroupEventAttendances] =
    useState<IGroupAttendances | null>(null);
  const [isResponseAttendanceError, setIsResponseAttendanceError] =
    useState<boolean>(false);
  const [isLoadingGroupEventAttendances, setIsLoadingGroupEventAttendances] =
    useState<boolean>(true);
  const [eventLocation, setEventLocation] = useState<
    IGroupLocation | undefined
  >();
  const [selectedDeleteOption, setSelectedDeleteOption] = useState<
    string | undefined
  >(undefined);
  const [isResponseError, setIsResponseError] = useState<boolean>(false);
  const channel = useSelector(channelSelector) as GroupChannel;
  const [isLoadingGroupMembers, setIsLoadingGroupMembers] = useState(true);
  const [openEditGroupEventModal, setIsOpenEditGroupEventModal] =
    useState<boolean>(false);
  const isManager = channel?.myRole === 'operator';
  let isFocused: boolean;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    isFocused = useIsFocused();
  } catch {
    isFocused = true; // Default to focused for web environments without navigation context
  }

  const ownerList = useSelector(ownerListSelector);
  const currentUser = useSelector(userSelector);
  const currentId = currentUser?.userId;
  const isManagerMember = (member: IGroupMember): boolean => {
    return ownerList.some(
      (m) =>
        m._embedded?.['chat-user']?._embedded?.['end-user']?.id ===
        member._embedded['end-user']?.id
    );
  };

  const [repeatEvent, setRepeatEvent] = useState<IGroupEvent | undefined>();
  // Messages to be deleted also in the chat feed if the event is deleted
  const groupEventChatList = useGetMessageIdForEventId(
    repeatingGroupEvent?.id ? repeatingGroupEvent?.id : groupEvent?.id ?? ''
  );
  const repeatingEventInstance = groupEvent?._embedded?.['repeating-event'];
  const repeatingEvent = groupEvent?.repetition_rules;
  const [newGroupEvent, setNewGroupEvent] = useState<IGroupEvent | undefined>();

  // Feature flag for showing the group attendance
  const { isEnabled: isGroupAttendanceEnabled, isReady } = useFeatureFlag(
    UNLEASH_TOGGLES.DISPATCH_GROUP_ATTENDANCE
  );
  const groupMembersLessThanFifty = members && members?.length < 50;
  const isEventPast = groupEvent?.start_at
    ? moment().isAfter(groupEvent.start_at)
    : false;
  const isAttendanceTaken = useMemo(() => {
    const headcountValue = headcounts?.count || 0;

    return headcountValue > 0;
  }, [headcounts?.count]);

  const showTakeAttendanceComponent = useMemo(() => {
    return isManager && isEventPast && isGroupAttendanceEnabled;
  }, [isManager, isEventPast, isGroupAttendanceEnabled]);

  const [isAttendanceModalVisible, setAttendanceModalVisible] = useState(false);
  const [attendanceMode, setAttendanceMode] = React.useState<'create' | 'edit'>(
    'create'
  );
  const [hasTriggeredTakeAttendance, setHasTriggeredTakeAttendance] =
    useState(false);
  const sortedMembers = useMemo(() => sortMembers(members), [members]);

  const attendances = useMemo(() => {
    return groupEventAttendances?._embedded?.attendances ?? [];
  }, [groupEventAttendances]);

  const { attendedMembers, notAttendedMembers, guestCount } = useMemo(() => {
    const attended: typeof sortedMembers = [];
    const notAttended: typeof sortedMembers = [];

    sortedMembers.forEach((member) => {
      const endUserId = member._embedded['end-user']?.id;

      const attendance = attendances.find(
        (a) => a._embedded?.['end-user']?.id === endUserId
      );

      if (attendance?.attended) {
        attended.push(member);
      } else {
        notAttended.push(member);
      }
    });

    return {
      attendedMembers: attended,
      notAttendedMembers: notAttended,
      guestCount: headcounts?._embedded?.headcounts?.[0]?.count || 0,
    };
  }, [sortedMembers, attendances, headcounts]);

  //****************************************************************************
  // Methods
  //****************************************************************************

  const showKitSnack = (msg: string, dur: number) => {
    setSnackOptions({ visible: true, message: msg, duration: dur });
  };

  const openMoreActionModal = () => {
    setMoreActionModal(true);
  };

  const closeMoreActionModal = () => {
    setMoreActionModal(false);
  };

  const fetchGroupMembers = useCallback(() => {
    const _fetchGroupMembers = async () => {
      const groupId = groupEvent?._embedded?.group?.id;

      if (!accessToken || !groupId) {
        return;
      }

      setIsLoadingGroupMembers(true);
      try {
        const response = await GroupsService.GetGroupMembers({
          token: accessToken,
          filters: [['group.id', groupId ?? '']],
          pages: [
            ['size', 51],
            ['number', 1],
          ],
        });

        if (response.body && response.body._embedded?.['group-members']) {
          setMembers(response.body._embedded['group-members']);
        } else {
          if (
            response.status &&
            !excludedStatusCodes.includes(response.status)
          ) {
            HealthMonitor.logError(response.message);
          }
        }
      } catch (error) {
        HealthMonitor.logError(error);
      } finally {
        setIsLoadingGroupMembers(false);
      }
    };
    _fetchGroupMembers();
  }, [accessToken, groupEvent]);

  const fetchRepeatGroupEvent = useCallback(() => {
    const _fetchRepeatGroupEvent = async (id: string) => {
      try {
        const response = await GroupsService.GetRepeatingGroupEvent({
          id,
          token: accessToken,
        });

        if (response.body) {
          setRepeatEvent(response.body);
        } else {
          if (
            response.status &&
            !excludedStatusCodes.includes(response.status)
          ) {
            HealthMonitor.logError(response.message);
          }
        }
      } catch (error) {
        HealthMonitor.logError(error);
      }
    };

    if (repeatingEventInstance) {
      _fetchRepeatGroupEvent(repeatingEventInstance.id);
    }
  }, [accessToken, repeatingEventInstance]);

  useEffect(() => {
    if (isFocused && channel && takeAttendanceQuery) {
      if (members.length === 0) {
        fetchGroupMembers();
      }
    }
  }, [
    isFocused,
    channel,
    dispatch,
    members.length,
    takeAttendanceQuery,
    fetchGroupMembers,
  ]);
  // Hooks
  useEffect(() => {
    fetchRepeatGroupEvent();
  }, [fetchRepeatGroupEvent]);

  const _onEditEvent = useCallback(() => {
    setMoreActionModal(false);
    if (Platform.OS === 'web' && !isSmall) {
      setIsOpenEditGroupEventModal(true);
    } else {
      setTimeout(() => {
        Keyboard.dismiss();
        navigation.navigate('channel_details_modal', {
          screen: 'create_event',
          params: {
            event: repeatingGroupEvent ? repeatingGroupEvent : groupEvent,
            activeMessage: activeMessage,
            setNewGroupEvent: setNewGroupEvent,
          },
        });
      }, 500);
    }
  }, [groupEvent, isSmall, navigation, activeMessage, repeatingGroupEvent]);

  const _onDeleteEvent = useCallback(() => {
    setMoreActionModal(false);

    setTimeout(() => {
      setEventToDelete(true);
    }, 500);
  }, []);

  const _onShareEvent = () => {
    setMoreActionModal(false);
    setTimeout(() => {
      setEventToShare(true);
    }, 500);
  };

  const _confirmDeleteEvent = useCallback(async () => {
    const deleteGroupEventService =
      (repeatEvent || repeatingEvent) && selectedDeleteOption === 'all'
        ? GroupsService.DeleteRepeatingGroupEvent
        : GroupsService.DeleteGroupEvent;
    try {
      const response = await deleteGroupEventService({
        id:
          selectedDeleteOption === 'all'
            ? repeatEvent?.id || groupEvent?.id
            : selectedDeleteOption === 'only' && repeatingEvent
            ? groupEvent?._embedded?.event?.[0].id
            : eventId,
        token: accessToken,
      });

      if (response.status === 204) {
        if (Platform.OS !== 'web') {
          setEventToDelete(false);
        }

        if (groupEventChatList && groupEventChatList.length > 0) {
          groupEventChatList.forEach((message) => {
            channel.deleteMessage(message);
          });
        }

        navigation.goBack();

        showKitSnack(t('groups:eventDeleted'), KitSnackDuration.SHORT);
      } else {
        if (response.status && !excludedStatusCodes.includes(response.status)) {
          HealthMonitor.logError(response.message);
        }
      }
    } catch (error) {
      HealthMonitor.logError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accessToken,
    eventId,
    groupEventChatList,
    repeatEvent,
    selectedDeleteOption,
  ]);

  const _confirmShareEvent = useCallback(async () => {
    setEventToShare(false);
    const sendEvent = (event: IGroupEvent | undefined) => {
      if (!event) {
        return;
      }

      dispatch(
        sendEventMessage(
          channel as GroupChannel,
          {
            groupEvent: event,
          },
          t('groups:eventShared')
        )
      );
    };

    setTimeout(() => {
      sendEvent(groupEvent);
      showKitSnack(t('groups:eventSharedToChat'), KitSnackDuration.LONG);
    }, 100);
  }, [channel, dispatch, groupEvent, t]);

  //****************************************************************************
  // Lifecycle
  //****************************************************************************
  const getUpdateGroupEventService = useCallback(() => {
    if (
      (newGroupEvent !== undefined &&
        newGroupEvent.repetition_rules !== undefined) ||
      (eventId && isRepeatingEvent)
    ) {
      return { getEventService: GroupsService.GetRepeatingGroupEvent };
    } else if (
      (newGroupEvent !== undefined &&
        newGroupEvent.repetition_rules === undefined) ||
      (eventId && !isRepeatingEvent)
    ) {
      return { getEventService: GroupsService.GetGroupEvent };
    } else if (newGroupEvent === undefined || (eventId && !isRepeatingEvent)) {
      return { getEventService: GroupsService.GetGroupEvent };
    }

    return { getEventService: GroupsService.GetGroupEvent };
  }, [isRepeatingEvent, eventId, newGroupEvent]);

  const fetchGroupEvent = useCallback(() => {
    const _fetchGroupEvent = async (id: string) => {
      const { getEventService } = getUpdateGroupEventService() as {
        getEventService?: EventServiceFunction;
      };

      setIsLoading(true);
      try {
        if (getEventService) {
          const response = await getEventService({
            id: newGroupEvent?.id ?? eventId,
            token: accessToken,
          });

          if (response.body) {
            setGroupEvent(response.body);
          } else {
            setIsResponseError(true);
            if (
              response.status &&
              !excludedStatusCodes.includes(response.status)
            ) {
              HealthMonitor.logError(response.message);
            }
          }
        }
      } catch (error) {
        HealthMonitor.logError(error);
      } finally {
        setIsLoading(false);
      }
    };

    _fetchGroupEvent(newGroupEvent?.id ?? eventId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, eventId, newGroupEvent, setNewGroupEvent]);

  // Hooks
  useEffect(() => {
    fetchGroupEvent();
  }, [fetchGroupEvent, newGroupEvent, isFocused]);

  useEffect(() => {
    if (isAttendanceTaken) {
      setAttendanceMode('edit');
    }
  }, [isAttendanceTaken]);
  // Fetching repeating group event data if exists
  useEffect(() => {
    const _fetchRepeatingGroupEvent = async (id: string) => {
      setIsLoading(true);
      try {
        const response = await GroupsService.GetRepeatingGroupEvent({
          id,
          token: accessToken,
        });

        if (response.body) {
          setRepeatingGroupEvent(response.body);
        } else {
          setIsResponseError(true);
          if (
            response.status &&
            !excludedStatusCodes.includes(response.status)
          ) {
            HealthMonitor.logError(response.message);
          }
        }
      } catch (error) {
        HealthMonitor.logError(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (groupEvent?._embedded?.['repeating-event']) {
      _fetchRepeatingGroupEvent(groupEvent?._embedded?.['repeating-event'].id);
    }
  }, [groupEvent, accessToken]);

  // Hooks
  useEffect(() => {
    if (groupEvent?._embedded?.location !== null) {
      setEventLocation(groupEvent?._embedded?.location as IGroupLocation);
    } else {
      setEventLocation(undefined);
    }
  }, [groupEvent?._embedded?.location, isFocused]);

  const prevParams = useRef<{
    isManager: boolean | null;
    eventExists: boolean | null;
    groupEvent: IGroupEvent | null | undefined;
  }>({
    isManager: null,
    eventExists: null,
    groupEvent: null,
  });
  useEffect(() => {
    // Check if any of the params changed
    if (
      prevParams.current.isManager !== isManager ||
      prevParams.current.groupEvent !== groupEvent
    ) {
      navigation?.setParams({
        openMoreActionModal,
        isManager,
        eventExists: groupEvent !== undefined,
        onEditEvent: _onEditEvent,
        onDeleteEvent: _onDeleteEvent,
        onShareEvent: _onShareEvent,
      });

      // Update the ref with the new values
      prevParams.current = {
        isManager,
        eventExists: groupEvent !== undefined,
        groupEvent,
      };
    }
  }, [navigation, isManager, groupEvent, _onEditEvent, _onDeleteEvent]);

  const fetchGroupEventHeadcounts = useCallback(() => {
    const _fetchGroupEventHeadcounts = async () => {
      try {
        const response = await GroupsService.GetGroupHeadCounts({
          token: accessToken,
          filters: [
            ['app_key', appKey],
            ['event.id', eventId],
          ],
        });

        if (response.body) {
          setHeadcounts(response.body);
        } else {
          console.warn('Could not fetch headcounts');
          setIsError(true);
        }
      } catch (error) {
        console.error('Error fetching headcounts:', error);
        setIsError(true);
      } finally {
        setIsLoadingHeadCounts(false);
      }
    };

    _fetchGroupEventHeadcounts();
  }, [accessToken, appKey, eventId]);

  const fetchGroupEventAttendances = useCallback(() => {
    const _fetchGroupEventAttendances = async () => {
      try {
        const response = await GroupsService.GetGroupEventAttendances({
          token: accessToken,
          filters: [
            ['app_key', appKey],
            ['event.id', eventId],
          ],
        });

        if (response.body) {
          setGroupEventAttendances(response.body);
        } else {
          debug('Could not fetch group events attendances');
          setIsResponseAttendanceError(true);
        }
      } catch {
      } finally {
        setIsLoadingGroupEventAttendances(false);
      }
    };

    _fetchGroupEventAttendances();
  }, [accessToken, appKey, eventId]);

  const handleAttendancePress = useCallback(
    (mode: 'create' | 'edit') => {
      if (Platform.OS === 'web') {
        setAttendanceMode(mode);
        setAttendanceModalVisible(true);
      } else {
        // Navigate to another screen on mobile
        navigation.navigate(Constants.CREATE_EDIT_GROUP_ATTENDANCE_SCREEN, {
          title:
            mode === 'edit'
              ? t('groups:editAttendance')
              : t('groups:takeAttendanceButton'),
          mode,
          headcounts,
          groupEventAttendances,
          groupEvent,
          onSuccess: () => {
            fetchGroupEvent();
            fetchGroupEventAttendances();
            fetchGroupEventHeadcounts();
          },
        });
      }
    },
    [
      navigation,
      t,
      headcounts,
      groupEventAttendances,
      groupEvent,
      fetchGroupEventAttendances,
      fetchGroupEventHeadcounts,
      fetchGroupEvent,
    ]
  );

  const handleCancel = () => {
    setAttendanceModalVisible(false);

    if (Platform.OS === 'web') {
      const queryParams = new URLSearchParams(location.search);

      if (queryParams.has('take_attendance')) {
        queryParams.delete('take_attendance');

        history.replace({
          pathname: location.pathname,
          search: queryParams.toString(),
        });
      }
    }
  };

  useEffect(() => {
    if (showTakeAttendanceComponent) {
      fetchGroupEventAttendances();
      fetchGroupEventHeadcounts();
    }
  }, [
    showTakeAttendanceComponent,
    fetchGroupEventAttendances,
    fetchGroupEventHeadcounts,
  ]);

  useEffect(() => {
    if (showTakeAttendanceComponent) {
      fetchGroupMembers();
    }
  }, [showTakeAttendanceComponent, fetchGroupMembers]);

  // Auto-open attendance modal when takeAttendance is true (only once)
  useEffect(() => {
    if (
      takeAttendance &&
      !hasTriggeredTakeAttendance &&
      groupEvent &&
      !isLoading &&
      !isLoadingHeadCounts &&
      !isLoadingGroupEventAttendances
    ) {
      // Small delay to ensure the screen is fully loaded
      const timer = setTimeout(() => {
        handleAttendancePress('create');
        setHasTriggeredTakeAttendance(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [
    takeAttendance,
    hasTriggeredTakeAttendance,
    groupEvent,
    isLoading,
    isLoadingHeadCounts,
    isLoadingGroupEventAttendances,
    handleAttendancePress,
  ]);

  return (
    <>
      {isLoading &&
      isLoadingHeadCounts &&
      isLoadingGroupEventAttendances &&
      isLoadingGroupMembers ? (
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: Colors.N0,
          }}
        >
          <KitLoader />
        </View>
      ) : isResponseError ? (
        <KitNotFound />
      ) : (
        <ScrollView style={{ backgroundColor: 'white' }}>
          <View
            style={[
              {
                maxWidth: '100%',
                marginTop: Platform.OS !== 'web' && isLandscape ? 14 : 0,
                borderTopWidth: Platform.OS !== 'web' && isLandscape ? 1 : 0,
                borderTopColor:
                  Platform.OS !== 'web' && isLandscape
                    ? Colors.N100
                    : Colors.N0,
              },
              styles.safeContainer,
            ]}
          >
            {showTakeAttendanceComponent && !isAttendanceTaken ? (
              <EventAttendanceBarComponent
                onPressAttendance={(mode) => {
                  setAttendanceMode(mode);
                  handleAttendancePress(mode);
                  setAttendanceModalVisible(true);
                }}
              />
            ) : null}

            <View
              style={{
                paddingHorizontal: isLarge ? Spacing.xxxxl + 8 : Spacing.l,
                paddingTop: Platform.OS !== 'web' ? Spacing.m : Spacing.xl,
                paddingBottom: groupMembersLessThanFifty
                  ? Spacing.xs
                  : Spacing.xl,
              }}
            >
              {/* Event Header */}
              {groupEvent ? (
                <EventDetailHeader
                  event={groupEvent}
                  eventLocation={eventLocation}
                />
              ) : null}
              {/* Event Location */}
              <EventDetailLocation
                eventLocation={eventLocation}
                // @ts-ignore
                marker={eventLocation?.title}
              />

              {/* Event Description */}
              {groupEvent?.description !== undefined &&
              groupEvent?.description !== '' ? (
                <View
                  style={{
                    position: 'relative',
                    marginTop: Spacing.xxl,
                  }}
                >
                  <KitText
                    fontSize={20}
                    bold
                    color={Colors.N900}
                    style={{ marginBottom: Spacing.xs }}
                  >
                    {t('eventsAbout')}
                  </KitText>
                  <KitText expandable numberOfLines={3}>
                    {groupEvent.description}
                  </KitText>
                </View>
              ) : null}

              {showTakeAttendanceComponent ? (
                <EventAttendanceComponent
                  isAttendanceTaken={isAttendanceTaken}
                  attendedCount={headcounts?._embedded?.headcounts?.[0]?.count}
                  onPressAttendance={handleAttendancePress}
                  groupMembersLessThanFifty={groupMembersLessThanFifty}
                  groupEvent={groupEvent}
                />
              ) : null}
            </View>
            {groupMembersLessThanFifty &&
            isAttendanceTaken &&
            !isLoadingGroupMembers ? (
              <View
                style={{
                  paddingHorizontal: isLarge ? Spacing.xxxxl + 8 : 0,
                  paddingBottom: Spacing.xl,
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-evenly',
                    marginBottom: Spacing.m,
                    marginTop: Spacing.l,
                  }}
                >
                  <KitTab
                    onPress={() => setActiveTab(0)}
                    active={activeTab === 0}
                    fontSize={14}
                  >
                    {`${t('groups:attended')} (${attendedMembers.length})`}
                  </KitTab>
                  <KitTab
                    onPress={() => setActiveTab(1)}
                    active={activeTab === 1}
                    fontSize={14}
                  >
                    {`${t('groups:missed')} (${notAttendedMembers.length})`}
                  </KitTab>
                </View>

                <KitList
                  data={activeTab === 0 ? attendedMembers : notAttendedMembers}
                  // disable animation if no list-items; otherwise it gets stuck animation
                  animationEnabled={Boolean(
                    activeTab === 0 && attendedMembers.length > 0
                  )}
                  renderItem={({ item, index }) => {
                    const nickname = `${
                      item._embedded['end-user']?.first_name
                    } ${item._embedded['end-user']?.last_name}${
                      item._embedded['end-user']?.id === currentId
                        ? ' (you)'
                        : ''
                    }`;
                    const photoId =
                      item?._embedded?.['end-user']?._embedded?.profiles?.[0]
                        ?._embedded?.photo?.id ?? '';
                    const imagePhotoUrl = createImageUrl(photoId);
                    const memberIsManager = isManagerMember(item);

                    return (
                      <KitListItem
                        ImageComponent={
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                            }}
                          >
                            <KitAvatar
                              style={{ marginLeft: Spacing.xs }}
                              nickname={nickname}
                              size={Spacing.xxl}
                              imageUrl={imagePhotoUrl}
                            />
                          </View>
                        }
                        innerPaddingHorizontal={Spacing.l}
                        minHeight={79}
                        title={nickname}
                        titleFontSize={16}
                        rightElement={
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            {memberIsManager ? (
                              <KitBadge
                                text={t('groups:labelGroupManager')}
                                size='m'
                              />
                            ) : null}
                          </View>
                        }
                        rightIcon=''
                      />
                    );
                  }}
                  ListFooterComponent={
                    guestCount > 0 && activeTab !== 1 ? (
                      <KitListItem
                        innerPaddingHorizontal={Spacing.l}
                        minHeight={79}
                        ImageComponent={
                          <KitIcon
                            name='eye'
                            color={Colors.N600}
                            size={20}
                            style={{ margin: Spacing.s + 2 }}
                          />
                        }
                        title={t('groups:countsGuests', { count: guestCount })}
                        titleFontSize={16}
                      />
                    ) : null
                  }
                />
              </View>
            ) : null}
          </View>
        </ScrollView>
      )}
      <KitSnackRender
        {...snackOptions}
        setVisible={(value) =>
          setSnackOptions({ ...snackOptions, visible: value })
        }
      />

      {Platform.OS === 'web' ? (
        <KitModal
          isVisible={isAttendanceModalVisible || takeAttendanceQuery}
          onClose={handleCancel}
          anchorBottom={isSmall}
          modalStyle={{
            width: '100%',
            maxWidth: 620,
            maxHeight: 600,
            padding: 0,
          }}
          coverScreen={true}
          enableScroll
          noScrollView
        >
          <CreateOrUpdateGroupAttendance
            route={route}
            mode={attendanceMode}
            onCancel={handleCancel}
            onSuccess={() => {
              fetchGroupEvent();
              fetchGroupEventHeadcounts();
              fetchGroupEventAttendances();
            }}
            headcounts={headcounts}
            attendances={groupEventAttendances}
            groupEvent={groupEvent}
          />
        </KitModal>
      ) : null}

      <KitModal
        modalStyle={{
          width: '100%',
          maxWidth: 620,
          maxHeight: 700,
          padding: 0,
        }}
        coverScreen={true}
        anchorBottom={isSmall}
        isVisible={openEditGroupEventModal}
        onClose={() => setIsOpenEditGroupEventModal(false)}
        enableScroll
        noScrollView
      >
        <CreateEditGroupEventScreen
          groupEventInWebPlatform={
            repeatingGroupEvent ? repeatingGroupEvent : groupEvent
          }
          onCancel={() => setIsOpenEditGroupEventModal(false)}
        />
      </KitModal>

      {Platform.OS !== 'web' ? (
        <>
          {/* More Action Modal */}
          <MoreActionModal
            isVisible={moreActionModal}
            isManager={isManager}
            onClose={closeMoreActionModal}
            onEditEvent={_onEditEvent}
            onDeleteEvent={_onDeleteEvent}
            onShareEvent={_onShareEvent}
          />
        </>
      ) : null}

      <DeleteEventConfirmModal
        isVisible={eventToDelete}
        onClose={() => setEventToDelete(false)}
        title={
          // @ts-ignore
          groupEvent?.repetition_rules !== undefined
            ? t('deleteRecurringEvent')
            : t('deleteEvent')
        }
        subtitle={t('groups:deleteRecurringEventDesc')}
        confirmAction={_confirmDeleteEvent}
        event={groupEvent ?? {}}
        repeatEvent={repeatEvent ?? {}}
        selectedOption={selectedDeleteOption}
        onOptionSelect={setSelectedDeleteOption}
      />

      <ShareEventConfirmModal
        isVisible={eventToShare}
        onClose={() => setEventToShare(false)}
        title={t('common:shareToChat')}
        subtitle={t('common:shareThisEventDescription')}
        confirmAction={_confirmShareEvent}
        event={groupEvent ?? {}}
      />
    </>
  );
}

//******************************************************************************
// Styles
//******************************************************************************
const styles = StyleSheet.create({
  viewContainer: {
    flex: 1,
    paddingHorizontal: Spacing.l,
  },
  safeContainer: {
    flex: 1,
    backgroundColor: colorForScheme({ default: Colors.N0 }),
  },
});
